import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'react-feather';
import { toast } from 'react-toastify';
import {
  fetchTasksMe,
  isTasksLeaderRole,
  RM_TASKS_ENABLED,
  tasksApi,
  TasksRole,
} from '../../../Tasks/tasksApi';
import {
  Badge, Button, Card, EmptyState, Field, Loader, Page, PageHeader, Table, TextInput, useConfirm,
} from '../../../ui';
import type { BadgeTone } from '../../../ui';
import { Callout, Drawer, IconButton } from './ui-pending';
import './RmLeavesPage.scss';

/**
 * Consumer Xperience → RM Leaves: who is away, and when.
 *
 * Leave is recorded here and nowhere else in this tool — deliberately not by
 * switching an RM inactive, which would hand their whole caseload to
 * somebody else permanently.
 *
 * Recording leave does not move any work. It tells the leader who to cover
 * for, and tells the learner-facing app that this RM is away. Reassigning
 * their open tickets is still a deliberate act on the task itself.
 */

interface AgentRow {
  id: string;
  externalId: string;
  name: string;
  email: string;
  role: TasksRole;
  isActive: boolean;
  availability?: 'ON_SHIFT' | 'OFF_SHIFT' | 'ON_LEAVE';
  onLeaveUntil?: string | null;
  /** The weekly roster, so leave can be counted in shifts as well as days. */
  workingDays?: string[] | null;
  team?: { key: string; name: string } | null;
}

interface Leave {
  id: string;
  startsOn: string;
  endsOn: string;
  note?: string | null;
  /** When it was booked — not when it starts. See `appliedAt`. */
  createdAt?: string;
}

/** An onboarding ticket that arrived while an RM was away. */
interface StrandedLead {
  id: string;
  taskNumber: number;
  customerName: string;
  customerExternalId: string;
  createdAt: string;
  stage: string;
}

const AVAIL_STYLE: Record<string, { label: string; tone: BadgeTone }> = {
  ON_SHIFT: { label: 'On shift', tone: 'success' },
  OFF_SHIFT: { label: 'Off shift', tone: 'neutral' },
  ON_LEAVE: { label: 'On leave', tone: 'danger' },
};

/** A @db.Date comes back as UTC midnight; show the calendar day, not a local shift. */
const dayOf = (iso: string): string => iso.slice(0, 10);

const pretty = (iso: string): string => {
  const [y, m, d] = dayOf(iso).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** Today in IST — leave is granted in IST calendar days. */
const istToday = (): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

/**
 * When the leave was actually booked, in IST, to the minute.
 *
 * It matters because leave entered after the day has started can't reroute
 * anything: assignment only knows what was recorded at the moment a lead
 * arrived. "Applied 2 Oct, 11:18" against leave *for* 2 Oct explains a
 * morning's worth of assignments at a glance.
 */
const appliedAt = (iso?: string): string | null => {
  if (!iso) return null;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
};

/** The IST calendar day a timestamp falls on — leave days are IST days, so
 *  comparing a raw UTC date string against one is wrong around midnight. */
const istDayOfInstant = (iso: string): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));

/** Booked on or after the first day it covers — too late to route around. */
const bookedLate = (l: Leave): boolean =>
  !!l.createdAt && istDayOfInstant(l.createdAt) >= dayOf(l.startsOn);

const rangeLabel = (l: Leave): string =>
  dayOf(l.startsOn) === dayOf(l.endsOn)
    ? pretty(l.startsOn)
    : `${pretty(l.startsOn)} – ${pretty(l.endsOn)}`;

const days = (l: Leave): number => {
  const a = Date.parse(dayOf(l.startsOn));
  const b = Date.parse(dayOf(l.endsOn));
  return Math.round((b - a) / 86400000) + 1;
};

const WEEK = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/**
 * The days in the range the RM would otherwise have worked.
 *
 * Leave booked Friday to Tuesday over a weekend off spans five calendar days
 * but costs three shifts. Reporting the larger number reads as though the
 * week off had been swallowed by the leave — which is what ops saw, and why
 * both are now shown when they differ. Nothing here changes the roster: the
 * week off is still the week off, and this only counts.
 */
const workingDaysIn = (l: Leave, roster?: string[] | null): number => {
  if (!roster?.length) return days(l);
  let n = 0;
  for (let t = Date.parse(dayOf(l.startsOn)); t <= Date.parse(dayOf(l.endsOn)); t += 86400000) {
    if (roster.includes(WEEK[new Date(t).getUTCDay()])) n += 1;
  }
  return n;
};

/** "3 working days · 5 calendar days", collapsing when they agree. */
const spanLabel = (l: Leave, roster?: string[] | null): string => {
  const total = days(l);
  const working = workingDaysIn(l, roster);
  const plural = (n: number) => (n === 1 ? '' : 's');
  if (!roster?.length || working === total) return `${total} day${plural(total)}`;
  return `${working} working day${plural(working)} · ${total} calendar day${plural(total)}`;
};

const RmLeavesPage: React.FC = () => {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  const [selected, setSelected] = useState<AgentRow | null>(null);
  const [leaves, setLeaves] = useState<Leave[] | null>(null);
  const [saving, setSaving] = useState(false);

  // The add/edit form. `editing` null = adding.
  const [editing, setEditing] = useState<Leave | null>(null);
  /** undefined = not looked for, null = looking, [] = none found. */
  const [stranded, setStranded] = useState<StrandedLead[] | null | undefined>(undefined);
  const [strandedFor, setStrandedFor] = useState<string>('');
  const [startsOn, setStartsOn] = useState('');
  const [endsOn, setEndsOn] = useState('');
  const [note, setNote] = useState('');
  const { confirm, dialog } = useConfirm();

  useEffect(() => {
    fetchTasksMe().then((me) => setAllowed(isTasksLeaderRole(me?.role)));
  }, []);

  const load = useCallback(async () => {
    try {
      const rows = await tasksApi<AgentRow[]>('/v1/agents');
      setAgents(rows.filter((a) => a.role === 'AGENT' || a.role === 'TEAM_LEADER'));
    } catch (err: any) {
      toast.error(err.message || 'Could not load RMs');
      setAgents([]);
    }
  }, []);

  useEffect(() => {
    if (allowed) void load();
  }, [allowed, load]);

  const openAgent = async (a: AgentRow) => {
    setSelected(a);
    setLeaves(null);
    setStranded(undefined); // belongs to whoever was open before
    resetForm();
    try {
      setLeaves(await tasksApi<Leave[]>(`/v1/agents/${a.externalId}/leaves`));
    } catch (err: any) {
      toast.error(err.message || 'Could not load leave');
      setLeaves([]);
    }
  };

  const resetForm = () => {
    setEditing(null);
    setStartsOn('');
    setEndsOn('');
    setNote('');
  };

  /**
   * New learners that already landed on this RM inside a leave window.
   *
   * Only onboarding tickets: those are the leads where nobody owned the
   * learner yet, so moving them is a real choice. Everything else on the
   * RM's queue belongs to learners who are already theirs, and a week off
   * isn't a reason to take those away.
   *
   * Nothing is reassigned here — a lead that arrived before the leave was
   * recorded can't be un-routed, and who should take it is a judgement only
   * the lead has. This just makes it visible on the day instead of surfacing
   * as a complaint later.
   */
  const findStrandedLeads = async (
    externalId: string,
    first: string,
    last: string,
  ): Promise<void> => {
    setStranded(null);
    try {
      const { tasks } = await tasksApi<{ tasks: StrandedLead[] }>(
        `/v1/tasks?assigneeExternalId=${encodeURIComponent(externalId)}` +
          `&taskTypeKey=onboarding&dateField=created&tz=Asia/Kolkata` +
          `&from=${first}&to=${last}&take=50`,
      );
      setStranded(tasks ?? []);
    } catch {
      // Not worth an error toast — the leave itself saved, and this is a
      // courtesy lookup on top of it.
      setStranded([]);
    }
  };

  const startEdit = (l: Leave) => {
    setEditing(l);
    setStartsOn(dayOf(l.startsOn));
    setEndsOn(dayOf(l.endsOn));
    setNote(l.note ?? '');
  };

  const save = async () => {
    if (!selected) return;
    if (!startsOn || !endsOn) return toast.error('Pick both dates');
    if (endsOn < startsOn) return toast.error('The last day cannot be before the first');
    setSaving(true);
    try {
      const body = JSON.stringify({ startsOn, endsOn, note: note.trim() });
      if (editing) {
        await tasksApi(`/v1/agents/${selected.externalId}/leaves/${editing.id}`, {
          method: 'PATCH',
          body,
        });
        toast.success('Leave updated');
      } else {
        await tasksApi(`/v1/agents/${selected.externalId}/leaves`, { method: 'POST', body });
        toast.success('Leave added');
      }
      // Leave that starts today or earlier can't have routed anything away —
      // check what already landed on them, before clearing the form.
      const lateBooking = startsOn <= istToday();
      const agentId = selected.externalId;
      const first = startsOn;
      const last = endsOn < istToday() ? endsOn : istToday();
      resetForm();
      setLeaves(await tasksApi<Leave[]>(`/v1/agents/${agentId}/leaves`));
      void load(); // availability may have changed if the range covers today
      if (lateBooking) {
        setStrandedFor(`${pretty(first)}${first === last ? '' : ` – ${pretty(last)}`}`);
        void findStrandedLeads(agentId, first, last);
      }
    } catch (err: any) {
      toast.error(err.message || 'Could not save leave');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (l: Leave) => {
    if (!selected) return;
    if (!(await confirm({
      title: `Cancel ${selected.name}'s leave on ${rangeLabel(l)}?`,
      confirmLabel: 'Cancel leave',
      cancelLabel: 'Keep it',
      tone: 'danger',
    }))) return;
    setSaving(true);
    try {
      await tasksApi(`/v1/agents/${selected.externalId}/leaves/${l.id}`, { method: 'DELETE' });
      toast.success('Leave cancelled');
      setLeaves(await tasksApi<Leave[]>(`/v1/agents/${selected.externalId}/leaves`));
      void load();
    } catch (err: any) {
      toast.error(err.message || 'Could not cancel leave');
    } finally {
      setSaving(false);
    }
  };

  const onLeaveCount = useMemo(
    () => (agents ?? []).filter((a) => a.availability === 'ON_LEAVE').length,
    [agents],
  );

  if (!RM_TASKS_ENABLED) {
    return (
      <Page>
        <EmptyState title="RM Leaves isn't available" description="This feature isn't enabled in this environment yet." />
      </Page>
    );
  }
  if (allowed === null) {
    return (
      <Page>
        <Loader label="Checking access…" />
      </Page>
    );
  }
  if (!allowed) {
    return (
      <Page>
        <EmptyState title="Team leads only" description="You need a team-lead or admin role to manage RM leave." />
      </Page>
    );
  }

  const today = istToday();
  const upcoming = (leaves ?? []).filter((l) => dayOf(l.endsOn) >= today);
  const past = (leaves ?? []).filter((l) => dayOf(l.endsOn) < today);

  return (
    <Page>
      {dialog}
      <PageHeader
        eyebrow="RM Tasks"
        title="RM Leaves"
        subtitle={
          <>
            Who&apos;s away, and when. Recording leave doesn&apos;t move anyone&apos;s tickets — it shows
            you who needs covering, and tells the learner app their RM is away.
            {onLeaveCount > 0 && (
              <>
                {' '}
                <strong>{onLeaveCount}</strong> {onLeaveCount === 1 ? 'RM is' : 'RMs are'} on leave today.
              </>
            )}
          </>
        }
      />

      <Card flush>
        {agents === null ? (
          <Loader label="Loading RMs…" />
        ) : agents.length === 0 ? (
          <EmptyState title="No RMs found" description="RMs and team leads provisioned in the tasks service appear here." />
        ) : (
          <Table caption="RMs and their leave">
            <thead>
              <tr>
                <th>RM</th>
                <th>Role</th>
                <th>Right now</th>
                <th>On leave until</th>
                <th className="rml-cell-end">Leave</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => {
                const st = AVAIL_STYLE[a.availability ?? 'OFF_SHIFT'];
                return (
                  <tr key={a.id}>
                    <td>
                      <div className="ds-strong">{a.name}</div>
                      <div className="rm-caption">{a.email}</div>
                    </td>
                    <td>{a.role.replace('_', ' ')}</td>
                    <td>
                      <Badge tone={st.tone}>{st.label}</Badge>
                      {!a.isActive && <span className="rm-caption rml-inactive">(inactive)</span>}
                    </td>
                    <td>{a.onLeaveUntil ? pretty(a.onLeaveUntil) : '—'}</td>
                    <td className="rml-cell-end">
                      <Button size="sm" variant="secondary" onClick={() => void openAgent(a)}>
                        Manage
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        placement="center"
        size="lg"
        title={`${selected?.name} — leave`}
        footer={<Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>}
      >
        <div className="rml-form">
          <h3 className="rml-heading">{editing ? 'Edit leave' : 'Add leave'}</h3>
          <div className="rml-form__row">
            <Field label="First day" className="rml-form__date">
              {(f) => (
                <TextInput
                  {...f}
                  type="date"
                  size="sm"
                  value={startsOn}
                  onChange={(e) => {
                    setStartsOn(e.target.value);
                    // A single day off is the common case — fill the end to match.
                    if (!endsOn || endsOn < e.target.value) setEndsOn(e.target.value);
                  }}
                />
              )}
            </Field>
            <Field label="Last day (included)" className="rml-form__date">
              {(f) => (
                <TextInput
                  {...f}
                  type="date"
                  size="sm"
                  value={endsOn}
                  min={startsOn || undefined}
                  onChange={(e) => setEndsOn(e.target.value)}
                />
              )}
            </Field>
            <Field label="Reason (optional)" className="rml-form__grow">
              {(f) => (
                <TextInput
                  {...f}
                  size="sm"
                  value={note}
                  placeholder="Annual leave, sick…"
                  onChange={(e) => setNote(e.target.value)}
                />
              )}
            </Field>
            <div className="rm-row">
              <Button
                size="sm"
                variant="primary"
                disabled={saving || !startsOn || !endsOn}
                onClick={() => void save()}
              >
                {saving ? 'Saving…' : editing ? 'Update' : 'Add'}
              </Button>
              {editing && (
                <Button size="sm" variant="secondary" disabled={saving} onClick={resetForm}>
                  Cancel
                </Button>
              )}
            </div>
          </div>
          {startsOn && endsOn && endsOn >= startsOn && (
            <div className="rm-caption">
              {spanLabel({ id: '', startsOn, endsOn }, selected?.workingDays)} away.
            </div>
          )}
        </div>

        {/* Booked for a day already under way: say what it couldn't stop.
            Assignment only knows the leave that existed when a lead
            arrived, so anything already routed stays routed. */}
        {stranded !== undefined && (
          <div className="rml-stranded">
            <Callout
              tone="warning"
              title="This leave starts today or earlier"
              action={
                <IconButton aria-label="Dismiss" onClick={() => setStranded(undefined)}>
                  <X size={16} aria-hidden />
                </IconButton>
              }
            >
              Routing only knows about leave that was already recorded, so new
              learners that arrived during {strandedFor} stayed with{' '}
              {selected?.name}. Booking it now stops any more coming.
            </Callout>
            {stranded === null ? (
              <Loader label="Checking for new learners…" />
            ) : stranded.length === 0 ? (
              <Callout tone="success">
                Nothing arrived in that window — no new learners to move.
              </Callout>
            ) : (
              <>
                <div className="ds-strong rml-small">
                  {stranded.length} new learner{stranded.length === 1 ? '' : 's'} landed on
                  them in that window
                </div>
                <Table caption="New learners routed during the leave">
                  <tbody>
                    {stranded.map((t) => (
                      <tr key={t.id}>
                        <td>
                          <div className="ds-strong">{t.customerName}</div>
                          <div className="rm-caption">
                            RMT-{String(t.taskNumber).padStart(4, '0')} ·{' '}
                            {appliedAt(t.createdAt)} IST · {t.stage === 'OPEN' ? 'not started' : t.stage === 'CLOSED' ? 'closed' : 'in progress'}
                          </div>
                        </td>
                        <td className="rml-cell-end">
                          <a
                            href={`${process.env.PUBLIC_URL}/pages/rm-tasks/${t.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ds-btn ds-btn--secondary ds-btn--sm"
                          >
                            Open ticket
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                <div className="rm-caption">
                  To hand a learner over for good, use <strong>Change RM</strong> on their profile —
                  reassigning the ticket alone moves the work but leaves the learner with{' '}
                  {selected?.name}, so their next ticket comes back here.
                </div>
              </>
            )}
          </div>
        )}

        {leaves === null ? (
          <Loader label="Loading leave…" />
        ) : (
          <div className="ds-stack">
            <h3 className="rml-heading">Upcoming &amp; current</h3>
            {upcoming.length === 0 ? (
              <EmptyState title="No leave booked" description="Add leave above to record when this RM is away." />
            ) : (
              <Table caption="Upcoming and current leave">
                <tbody>
                  {upcoming.map((l) => (
                    <tr key={l.id}>
                      <td>
                        <div className="ds-strong">{rangeLabel(l)}</div>
                        <div className="rm-caption">
                          {spanLabel(l, selected?.workingDays)}
                          {l.note ? ` · ${l.note}` : ''}
                          {dayOf(l.startsOn) <= today && today <= dayOf(l.endsOn)
                            ? ' · on leave now'
                            : ''}
                        </div>
                      </td>
                      <td className="rml-cell-end">
                        <div className="rml-actions">
                          <Button size="sm" variant="secondary" disabled={saving}
                            onClick={() => startEdit(l)}>
                            Edit
                          </Button>
                          <Button size="sm" variant="danger" disabled={saving}
                            onClick={() => void remove(l)}>
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}

            {past.length > 0 && (
              <details className="rml-past">
                <summary className="rml-past__summary">
                  Past leave ({past.length})
                </summary>
                <Table caption="Past leave">
                  <tbody>
                    {past.map((l) => (
                      <tr key={l.id}>
                        <td className="ds-text-muted">
                          {rangeLabel(l)}
                          {l.note ? ` · ${l.note}` : ''}
                          {/* When it was booked, not when it started — the
                              difference explains who got what that day. */}
                          {appliedAt(l.createdAt) && (
                            <div className="rm-caption">
                              Applied {appliedAt(l.createdAt)} IST
                              {bookedLate(l) && (
                                <span className="rml-late">
                                  {' '}· on or after the leave had begun, so leads
                                  that day were still routed to them
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="rml-cell-end">
                          <Button size="sm" variant="danger" disabled={saving}
                            onClick={() => void remove(l)}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </details>
            )}
          </div>
        )}
      </Drawer>
    </Page>
  );
};

export default RmLeavesPage;
